import { useState, useEffect } from 'react';
import { Input } from './Input';
import { Button } from './Button';
import { UserPlus, Save } from 'lucide-react';
import type { Contact } from '../../types';

export interface ContactFormData {
  name: string;
  email: string;
  phone: string;
  function: string;
  commentary: string;
  is_generic: boolean;
  flags: 'do_not_contact' | 'expired' | 'auto_discovered' | 'gatekeeper' | 'none';
}

export const initialContactFormData: ContactFormData = {
  name: '',
  email: '',
  phone: '',
  function: '',
  commentary: '',
  is_generic: false,
  flags: 'none',
};

interface ContactFormProps {
  mode: 'create' | 'edit';
  contact?: Contact | null;
  onSubmit: (data: ContactFormData) => Promise<void>;
  onCancel: () => void;
  error?: string;
  /** If true, shows a compact version with fewer fields */
  compact?: boolean;
  /** Create mode only: fields to prefill (e.g. an email picked from a message). Keep the object
   *  identity stable across renders — a new object resets the form. */
  initialData?: Partial<ContactFormData>;
  /** The form edits a contact at one venue: Gatekeeper is a role there, not everywhere. */
  venueScoped?: boolean;
}

export function ContactForm({
  mode,
  contact,
  onSubmit,
  onCancel,
  error,
  compact = false,
  initialData,
  venueScoped = false,
}: ContactFormProps) {
  const [formData, setFormData] = useState<ContactFormData>(initialContactFormData);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [localError, setLocalError] = useState('');

  useEffect(() => {
    if (mode === 'edit' && contact) {
      setFormData({
        name: contact.name || '',
        email: contact.email || '',
        phone: contact.phone || '',
        function: contact.function || '',
        commentary: contact.commentary || '',
        is_generic: contact.is_generic || false,
        flags: contact.flags || 'none',
      });
    } else {
      setFormData({ ...initialContactFormData, ...initialData });
    }
    setLocalError('');
  }, [mode, contact, initialData]);

  const hasAnyField = formData.name.trim() || formData.function.trim() || formData.email.trim() || formData.phone.trim();

  const handleSubmit = async () => {
    if (!hasAnyField) {
      setLocalError('At least one of name, role, email, or phone is required');
      return;
    }

    setIsSubmitting(true);
    setLocalError('');

    try {
      await onSubmit(formData);
    } catch (err: unknown) {
      const error = err as { response?: { data?: { detail?: string | Array<{ msg: string }> | { message?: string } } } };
      const detail = error.response?.data?.detail;
      if (typeof detail === 'string') {
        setLocalError(detail);
      } else if (Array.isArray(detail)) {
        setLocalError(detail.map(e => e.msg).join(', '));
      } else if (detail?.message) {
        setLocalError(detail.message);
      } else {
        setLocalError(`Failed to ${mode} contact`);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const displayError = error || localError;

  return (
    <div className="space-y-2">
      {displayError && (
        <div className="p-2 bg-red-900/20 border border-red-800/30 rounded-sm text-red-400 text-xs">
          {displayError}
        </div>
      )}

      {/* Row 1: Name | Role */}
      <div className="grid grid-cols-2 gap-2">
        <Input
          label="Name"
          value={formData.name}
          onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
          placeholder="Contact name"
        />
        <Input
          label="Role"
          value={formData.function}
          onChange={(e) => setFormData(prev => ({ ...prev, function: e.target.value }))}
          placeholder="e.g. Booking Manager"
        />
      </div>

      {/* Row 2: Email | Phone */}
      <div className="grid grid-cols-2 gap-2">
        <Input
          label="Email"
          type="email"
          value={formData.email}
          onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
          placeholder="contact@example.com"
        />
        <Input
          label="Phone"
          type="tel"
          value={formData.phone}
          onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
          placeholder="+1 234 567 8900"
        />
      </div>

      {!compact && (
        <>
          <div>
            <label className="block text-sm font-medium text-dark-text-secondary mb-1">
              Commentary
            </label>
            <textarea
              value={formData.commentary}
              onChange={(e) => setFormData(prev => ({ ...prev, commentary: e.target.value }))}
              placeholder="Additional notes..."
              className="w-full px-3 py-2 bg-dark-elevated border border-dark-border rounded-lg text-dark-text-primary placeholder:text-dark-text-muted focus:outline-hidden focus:ring-2 focus:ring-primary-500 min-h-[60px] text-sm"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-dark-text-secondary mb-2">
              Flags
            </label>
            <div className="flex flex-wrap gap-2">
              {[
                { value: 'none', label: 'None' },
                { value: 'gatekeeper', label: 'Gatekeeper' },
                { value: 'do_not_contact', label: 'Do Not Contact' },
                { value: 'expired', label: 'Expired' },
              ].map(({ value, label }) => (
                <label
                  key={value}
                  className={`flex items-center gap-2 px-3 py-1.5 rounded-lg cursor-pointer text-xs border transition-colors ${
                    formData.flags === value
                      ? 'bg-primary-900/30 border-primary-700/50 text-primary-400'
                      : 'bg-dark-elevated border-dark-border text-dark-text-secondary hover:bg-dark-border'
                  }`}
                >
                  <input
                    type="radio"
                    name="flags"
                    value={value}
                    checked={formData.flags === value}
                    onChange={(e) => setFormData(prev => ({ ...prev, flags: e.target.value as ContactFormData['flags'] }))}
                    className="sr-only"
                  />
                  {label}
                </label>
              ))}
            </div>
            {venueScoped && (
              <p className="mt-1.5 text-xs text-dark-text-muted">
                Gatekeeper applies to this venue only. Do Not Contact and Expired apply to the address everywhere.
              </p>
            )}
          </div>
        </>
      )}

      <div className="flex gap-2 pt-1">
        <Button
          type="button"
          size="sm"
          onClick={handleSubmit}
          isLoading={isSubmitting}
          disabled={!hasAnyField}
        >
          {mode === 'create' ? (
            <>
              <UserPlus className="w-3 h-3" />
              Create Contact
            </>
          ) : (
            <>
              <Save className="w-3 h-3" />
              Save
            </>
          )}
        </Button>
        <Button type="button" size="sm" variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </div>
  );
}
